import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env";
import { IAddress } from "@models";
import { lastValueFrom } from "rxjs";

/** Address used by USPS API */
interface PostalAddress {
  streetAddress: string;
  secondaryAddress: string;
  city: string;
  state: string;
  ZIPCode: string;
  ZIPPlus4: string;

  // extra items in the response that we don't use
  // streetAddressAbbreviation?: string;
  // cityAbbreviation?: string;
  // urbanization?: string;
}

/** Success response from the ValidateAddress api */
interface PostalAddressResponse {
  address: PostalAddress;
  /** Name of company at the address, if known */
  firm: string;
  additionalInfo: any;
  /** Indicates problems with the request - not unique address, etc. */
  corrections: { code: string, text: string }[];
  /** Indicates single/multiple/no match.  code 31 = exact match */
  matches: { code: string, text: string }[];
}

/** Error response from the USPS API */
interface PostalErrorResponse {
  apiVersion: string;
  error: {
    /** 400 = address not found or bad data */
    code: string;
    message: string;
    errors: { title: string, detail: string, code: string }[];
  }
}

/** Validates and corrects addresses using the US Postal Service API. */
@Injectable({ providedIn: 'root' })
export class AddressValidationService {
  constructor(private http: HttpClient) { }

  /** Validate the address and optionally fix the address in the entity.
   * @returns a string with an error message if address is invalid, or null if address is good.
   * Note that address can only be fixed if a match is found.
   */
  async validateEntityAddress(entity: IAddress, fix: boolean) {
    const pa = this.makePostalAddress(entity);
    const res = await this.callValidationApi(pa);
    const msg = this.getMessageFromResponse(res);
    if (fix && !msg) {
      this.setAddressFields(entity, (res as PostalAddressResponse).address);
    }
    return msg;
  }

  /** Call the ValidateAddress API on the server, which passes it through to the USPS API and returns the raw response. */
  private async callValidationApi(pa: PostalAddress) {
    const url = environment.apiUrl + "Postal/ValidateAddress";
    const res = await lastValueFrom(this.http.post<PostalAddressResponse | PostalErrorResponse>(url, pa));
    return res;
  }

  /** Get corrections text or error message from response.  Null indicates success. */
  private getMessageFromResponse(res: PostalAddressResponse | PostalErrorResponse) {
    if ((res as PostalErrorResponse).error) {
      return "ERROR: " + (res as PostalErrorResponse).error.message;
    }
    res = res as PostalAddressResponse;
    const text = (res.corrections.length && res.corrections[0].text);
    return text || null;
  }

  /** Extract address fields (`line1`, `city` etc.) from the entity and make a PostalAddress. */
  private makePostalAddress(entity: IAddress) {
    const ezip = entity.zipcode || '';
    const zip = ezip.slice(0, 5).replace(/[^0-9]*/, '');
    const p4 = ezip.slice(6).replace(/[^0-9]*/, '');

    const pa: PostalAddress = {
      streetAddress: entity.line1 || '',
      secondaryAddress: entity.line2 || '' + (entity.line3 ? ', ' + entity.line3 : ''),
      city: entity.city || '',
      state: entity.state || '',
      ZIPCode: zip,
      ZIPPlus4: p4,
    }
    return pa;
  }

  /** Set address fields (`line1` etc.) of the entity from the PostalAddress. */
  private setAddressFields(entity: IAddress, pa: PostalAddress) {
    entity.line1 = pa.streetAddress;
    entity.line2 = pa.secondaryAddress;
    entity.city = pa.city;
    entity.state = pa.state;
    entity.zipcode = pa.ZIPCode + (pa.ZIPPlus4 ? ('-' + pa.ZIPPlus4) : '');
    return entity;
  }
}
